import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, Package, FileText, DollarSign, MessageCircle, Plus, RefreshCw, Loader2, Globe, Bell } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Control = ({ setCurrentSection }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/stats/dashboard`);
      const data = await res.json();
      setStats(data);
    } catch (e) {
      console.error("Erro ao carregar stats:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const s = stats || {
    usuarios: { total: 0, fornecedores: 0, afiliados: 0, admins: 0 },
    produtos: { total: 0, ativos: 0 },
    pedidos: { total: 0, pendentes: 0, aprovados: 0 },
    financeiro: { faturamento: 0, comissoes: 0, total_comissoes: 0 },
    "integrações": { oauth_tokens: 0, conversas_ia: 0 },
    wms: { produtos: 0, notificacoes: 0 },
    usuarios_recentes: [],
  };

  return (
    <div className="p-8" data-testid="control-panel">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <div className="inline-block mb-3">
            <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
              Administracao
            </span>
          </div>
          <h1 className="text-4xl font-bold text-white">Painel de Controle</h1>
          <p className="text-base text-gray-400 mt-1">Dados reais do MongoDB em tempo real</p>
        </div>
        <Button onClick={fetchStats} disabled={loading} variant="outline" size="sm" className="border-gray-700" data-testid="refresh-stats-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-2">Atualizar</span>
        </Button>
      </div>

      {/* Metricas Gerais */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 border-none">
          <div className="text-center">
            <Users className="w-5 h-5 mx-auto mb-1.5 text-white/80" />
            <p className="text-white/80 text-xs mb-0.5">Fornecedores</p>
            <p className="text-white text-2xl font-bold" data-testid="count-fornecedores">{s.usuarios.fornecedores}</p>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 p-4 border-none">
          <div className="text-center">
            <Users className="w-5 h-5 mx-auto mb-1.5 text-white/80" />
            <p className="text-white/80 text-xs mb-0.5">Afiliados</p>
            <p className="text-white text-2xl font-bold" data-testid="count-afiliados">{s.usuarios.afiliados}</p>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 p-4 border-none">
          <div className="text-center">
            <Package className="w-5 h-5 mx-auto mb-1.5 text-white/80" />
            <p className="text-white/80 text-xs mb-0.5">Produtos</p>
            <p className="text-white text-2xl font-bold" data-testid="count-produtos">{s.produtos.total}</p>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-green-600 p-4 border-none">
          <div className="text-center">
            <FileText className="w-5 h-5 mx-auto mb-1.5 text-white/80" />
            <p className="text-white/80 text-xs mb-0.5">Pedidos</p>
            <p className="text-white text-2xl font-bold" data-testid="count-pedidos">{s.pedidos.total}</p>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 p-4 border-none">
          <div className="text-center">
            <DollarSign className="w-5 h-5 mx-auto mb-1.5 text-white/80" />
            <p className="text-white/80 text-xs mb-0.5">Faturamento</p>
            <p className="text-white text-xl font-bold">R$ {s.financeiro.faturamento.toLocaleString('pt-BR')}</p>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-pink-500 to-pink-600 p-4 border-none">
          <div className="text-center">
            <MessageCircle className="w-5 h-5 mx-auto mb-1.5 text-white/80" />
            <p className="text-white/80 text-xs mb-0.5">Comissoes</p>
            <p className="text-white text-xl font-bold">R$ {s.financeiro.comissoes.toLocaleString('pt-BR')}</p>
          </div>
        </Card>
      </div>

      {/* Gestao */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="bg-gray-800/50 border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" /> Fornecedores
            </h2>
            <Button size="sm" className="bg-blue-500 hover:bg-blue-600" data-testid="add-fornecedor-btn">
              <Plus className="w-4 h-4 mr-1" /> Novo
            </Button>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Total de Fornecedores</p>
                <p className="text-xs text-gray-400">Cadastrados no sistema</p>
              </div>
              <span className="text-xl font-bold text-blue-400">{s.usuarios.fornecedores}</span>
            </div>
          </div>
        </Card>

        <Card className="bg-gray-800/50 border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-500" /> Afiliados
            </h2>
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600" data-testid="add-afiliado-btn">
              <Plus className="w-4 h-4 mr-1" /> Novo
            </Button>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Total de Afiliados</p>
                <p className="text-xs text-gray-400">Cadastrados no sistema</p>
              </div>
              <span className="text-xl font-bold text-orange-400">{s.usuarios.afiliados}</span>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="bg-gray-800/50 border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Package className="w-5 h-5 text-purple-500" /> Produtos
            </h2>
            <Button size="sm" className="bg-purple-500 hover:bg-purple-600" onClick={() => setCurrentSection && setCurrentSection("catalog")}>
              Ver Catalogo
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-gray-900/50 rounded-lg">
              <p className="text-xs text-gray-400 mb-1">Total</p>
              <p className="text-lg font-bold text-purple-400">{s.produtos.total}</p>
            </div>
            <div className="p-3 bg-gray-900/50 rounded-lg">
              <p className="text-xs text-gray-400 mb-1">Ativos</p>
              <p className="text-lg font-bold text-green-400">{s.produtos.ativos}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-gray-800/50 border-gray-700 p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-green-500" /> Pedidos
          </h2>
          <div className="grid grid-cols-3 gap-2">
            <div className="p-2 bg-gray-900/50 rounded-lg text-center">
              <p className="text-xs text-gray-400 mb-1">Pendentes</p>
              <p className="text-lg font-bold text-yellow-400">{s.pedidos.pendentes}</p>
            </div>
            <div className="p-2 bg-gray-900/50 rounded-lg text-center">
              <p className="text-xs text-gray-400 mb-1">Aprovados</p>
              <p className="text-lg font-bold text-green-400">{s.pedidos.aprovados}</p>
            </div>
            <div className="p-2 bg-gray-900/50 rounded-lg text-center">
              <p className="text-xs text-gray-400 mb-1">Total</p>
              <p className="text-lg font-bold text-blue-400">{s.pedidos.total}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Integracoes e Infra */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="bg-gray-800/50 border-gray-700 p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-teal-500" /> Integracoes
          </h2>
          <div className="space-y-2">
            <div className="flex justify-between p-2 bg-gray-900/50 rounded-lg">
              <span className="text-sm text-gray-300">Tokens OAuth</span>
              <span className="font-bold text-teal-400">{s["integrações"]?.oauth_tokens || 0}</span>
            </div>
            <div className="flex justify-between p-2 bg-gray-900/50 rounded-lg">
              <span className="text-sm text-gray-300">Conversas IA</span>
              <span className="font-bold text-purple-400">{s["integrações"]?.conversas_ia || 0}</span>
            </div>
          </div>
        </Card>

        <Card className="bg-gray-800/50 border-gray-700 p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-yellow-500" /> Comissoes
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-gray-900/50 rounded-lg">
              <p className="text-xs text-gray-400 mb-1">Total</p>
              <p className="text-lg font-bold text-yellow-400">{s.financeiro.total_comissoes}</p>
            </div>
            <div className="p-3 bg-gray-900/50 rounded-lg">
              <p className="text-xs text-gray-400 mb-1">Valor</p>
              <p className="text-lg font-bold text-green-400">R$ {s.financeiro.comissoes.toLocaleString('pt-BR')}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-gray-800/50 border-gray-700 p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-pink-500" /> WMS & Notificacoes
          </h2>
          <div className="space-y-2">
            <div className="flex justify-between p-2 bg-gray-900/50 rounded-lg">
              <span className="text-sm text-gray-300">Produtos WMS</span>
              <span className="font-bold text-teal-400">{s.wms?.produtos || 0}</span>
            </div>
            <div className="flex justify-between p-2 bg-gray-900/50 rounded-lg">
              <span className="text-sm text-gray-300">Notificacoes</span>
              <span className="font-bold text-pink-400">{s.wms?.notificacoes || 0}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Usuarios Recentes */}
      {s.usuarios_recentes && s.usuarios_recentes.length > 0 && (
        <Card className="bg-gray-800/50 border-gray-700 p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-cyan-500" /> Usuarios Recentes
          </h2>
          <div className="space-y-2">
            {s.usuarios_recentes.map((u, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
                    ${u.tipo === 'admin' ? 'bg-red-500/20 text-red-400' : u.tipo === 'fornecedor' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}`}>
                    {u.nome?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{u.nome}</p>
                    <p className="text-xs text-gray-400">{u.email}</p>
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize
                  ${u.tipo === 'admin' ? 'bg-red-500/20 text-red-400' : u.tipo === 'fornecedor' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}`}>
                  {u.tipo}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default Control;
